import { Tr, Td, Text, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import * as _ from 'lodash';
import React, { useState } from 'react';
import { CSVLink } from 'react-csv';
import { useNavigate } from 'react-router-dom';

import { createExtendedResponseTape } from '@finley-technologies/calc-agent';
import { finleyAssert } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { AssignmentReceivableGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { assignmentReceivablesToBorrowingBaseReceivables } from './utils';
import { DownloadIconButton } from 'packages/common-frontend/components/IconButton';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useGetCurrentCreditAgreementFullArtifactQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { parseBorrowingBaseMechanics } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/utils';
import {
  EmptyDownloadAssignmentPreviewButton,
  LoadingDownloadAssignmentPreviewButton,
} from 'packages/loanProduct/ui/DownloadPreviewLoadingButtons';

export const DownloadExtendedResponseTapePreviewButton = ({
  creditFacilityId,
  proposedReceivables,
  previouslyAssignedReceivables,
  fileVersion,
  reportingDate,
  buttonLabel,
  fileNamePrefix,
}: {
  creditFacilityId: string;
  proposedReceivables: AssignmentReceivableGQLType[];
  previouslyAssignedReceivables?: AssignmentReceivableGQLType[];
  fileVersion: number;
  reportingDate: Util.FullDateStr;
  buttonLabel: string;
  fileNamePrefix: string;
}) => {
  const toast = useToast();
  const navigate = useNavigate();

  const [
    extendedResponseTapeRows,
    setExtendedResponseTapeRows,
  ] = useState<Util.SanitizedCsv>([]);
  const [
    extendedResponseTapeLoading,
    setIsExtendedResponseTapeLoading,
  ] = useState(false);

  const {
    data: creditAgreementData,
    loading: creditAgreementLoading,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: { creditFacilityId },
  });

  if (
    creditAgreementLoading ||
    !creditAgreementData ||
    !creditAgreementData.currentCreditAgreementFullArtifact
  ) {
    return <LoadingDownloadAssignmentPreviewButton buttonLabel={buttonLabel} />;
  }

  if (_.isEmpty(_.concat(previouslyAssignedReceivables, proposedReceivables))) {
    return <EmptyDownloadAssignmentPreviewButton buttonLabel={buttonLabel} />;
  }

  const {
    currentCreditAgreementFullArtifact: { creditAgreement, id },
  } = creditAgreementData;

  let parsedCreditAgreement = null;

  try {
    parsedCreditAgreement = parseCreditAgreementFullArtifact(creditAgreement);
    finleyAssert(
      parsedCreditAgreement,
      `Expect creditAgreementFullArtifact to exist for creditAgreementId ${id}`,
    );

    const {
      loanProductMechanics,
      borrowingBaseMechanics,
    } = parseBorrowingBaseMechanics(parsedCreditAgreement);

    const { multiLoanProductConcentrationLimits } = borrowingBaseMechanics;

    const { facilityType } = parsedCreditAgreement;

    const borrowingBaseReceivables = assignmentReceivablesToBorrowingBaseReceivables(
      {
        assignmentReceivables: previouslyAssignedReceivables
          ? _.concat(proposedReceivables, previouslyAssignedReceivables)
          : proposedReceivables,
        creditFacilityId,
      },
    );
    const downloadExtendedResponseTapePreviewCallback = async () => {
      setIsExtendedResponseTapeLoading(true);
      const { extendedResponseTape } = await createExtendedResponseTape({
        facilityType,
        multiLoanProductConcentrationLimits,
        loanProductMechanics,
        borrowingBaseReceivables,
      });

      setExtendedResponseTapeRows(extendedResponseTape);
      setIsExtendedResponseTapeLoading(false);
    };

    const getDataFromState = () => {
      return extendedResponseTapeRows;
    };

    const fileName = `${fileNamePrefix}-${reportingDate}-v${fileVersion}`;

    return (
      <Tr>
        <Td width="80%">
          <Text fontSize="12px">{buttonLabel}</Text>
        </Td>
        <Td pl="0">
          {/* !TODO @eldarshamukhmedov react-csv is no longer maintained, needs to be replaced */}
          <CSVLink
            data={getDataFromState()}
            asyncOnClick
            onClick={downloadExtendedResponseTapePreviewCallback}
            filename={fileName}
          >
            <DownloadIconButton
              label={buttonLabel}
              ariaLabel={buttonLabel || 'download csv'}
              isLoading={extendedResponseTapeLoading}
              onClick={() => {
                Sentry.addBreadcrumb({
                  category: 'ui.click',
                  message: `Clicked Export CSV to download ${fileName}`,
                  level: Sentry.Severity.Info,
                });
              }}
            />
          </CSVLink>
        </Td>
      </Tr>
    );
  } catch (err) {
    errorToastConfig.title = `Error parsing credit agreement artifact for credit agreement ${id}`;
    toast(errorToastConfig);
    navigate('/');
  }

  throw new Error('unreachable code here');
};
